import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchPublic, useApiRoot, usePublicRevalidate } from './fetchPublic';

/**
 * @typedef {Object} PublicProjectImage
 * @property {string} imageUrl
 * @property {number} width
 * @property {number} height
 */

/**
 * @typedef {Object} PublicProject
 * @property {number} id
 * @property {string} title
 * @property {string} groupSize
 * @property {string} frequency
 * @property {string} commitmentNotes
 * @property {string} site
 * @property {string} activity
 * @property {string[]} student_ages
 * @property {string[]} subject_areas
 * @property {string[]} career_experience
 * @property {string[]} date
 * @property {string[]} participation_options
 * @property {string[]} [commitment]
 * @property {string} [city]
 * @property {string} [state]
 * @property {boolean} [completed]
 * @property {PublicProjectImage} [image]
 */

/**
 * Custom hook for the public, shareable view of a project.
 * GET /public/project/{projectId} — unauthenticated, so no auth headers.
 * Note: apiRoot already includes the /community_v3 prefix.
 *
 * @param {number|string} projectId
 * @returns {import('@tanstack/react-query').UseQueryResult & { project: PublicProject|null, revalidate: Function, refresh: Function }}
 */
export default function usePublicProject(projectId) {
  const apiRoot = useApiRoot();
  const queryClient = useQueryClient();
  const url = `${apiRoot}/public/project/${projectId}`;

  const query = useQuery({
    queryKey: ['public_project', projectId],
    queryFn: () => fetchPublic(url),
    enabled: Boolean(projectId),
  });

  const revalidate = usePublicRevalidate(url);

  // Pull a fresh copy and update the rendered data. 'reload' bypasses the
  // browser HTTP cache (the public endpoint sends Cache-Control: max-age=300),
  // so this reflects a just-saved change instead of the cached response. Unlike
  // `revalidate`, which only warms the cache for a future reload, this writes
  // the result into react-query so the live page re-renders with every changed
  // field — used when a cross-tab broadcast signals the project changed.
  const refresh = useCallback(async () => {
    try {
      const data = await fetchPublic(url, { cache: 'reload' });
      queryClient.setQueryData(['public_project', projectId], data);
    } catch {
      // Best-effort: a failed refresh leaves the current data in place.
    }
  }, [url, projectId, queryClient]);

  return {
    ...query,
    project: query.data ?? null,
    revalidate,
    refresh,
  };
}
